<template>
  <v-container fluid class="pa-0">

    <entity-view-filter>
      <template v-slot:filter1>
        <date-range-picker
            v-model="dateRange"
            @change="updateCalendarEvents"/>
      </template>
      <template v-slot:filter2>
        <div class="d-inline-block">
          <v-checkbox :label="$t('plannedService.calendarEvents.filter.onlyCurrentUserAssigned')"
                      v-model="onlyCurrentUserAssigned" dense
                      @change="updateCalendarEvents"
                      class="ma-0 pa-0"/>
          <v-checkbox :label="$t('plannedService.calendarEvents.filter.includeLate')"
                      v-model="includeLate" dense
                      class="ma-0 pa-0"
                      @change="updateCalendarEvents"/>
        </div>
      </template>
    </entity-view-filter>
    <v-data-table v-if="$vuetify.breakpoint.name !== 'xs'"
                  class="elevation-1"
                  show-select
                  v-model="selectedRows"
                  :headers="tableHeaders"
                  item-key="calendarEvent.plannedService.plannedServiceInstanceId"
                  :items="tableRows"
                  :hide-default-footer="true"
                  :disable-pagination="true"
                  :disable-sort="true"
                  :loading="loading"
                  :loading-text="''"
                  :no-data-text="$t('plannedService.calendarEvents.noEventsOnDateRange')"
                  @contextmenu:row="rowRightClickHandler">

      <template v-slot:item.plannedServiceName="{ item }">
        <router-link :to="`plannedservices/${item.calendarEvent.plannedService.id}`">{{ item.plannedServiceName }}
        </router-link>
      </template>
      <template v-slot:item.serviceManagers="{ item }">
        <plain-unordered-list :items="item.serviceManagers"/>
      </template>
      <template v-slot:item.authorUsers="{ item }">
        <plain-unordered-list :items="item.authorUsers"/>
      </template>
      <template v-slot:item.rowActions="{ item }">
        <entity-table-row-end-action-menu
            class="text-end"
            :disabled="loading"
            :actions="item.rowActions"
        />
      </template>
      <template v-slot:footer>
        <div class="v-data-footer justify-start">
          <entity-table-selected-rows-action-menu
              :actions="selectedRowsActions"/>
        </div>
      </template>
    </v-data-table>

    <v-list v-if="$vuetify.breakpoint.name === 'xs'">
      <planned-service-event-list-item v-for="item in tableRows"
                                       :row-click-enabled="true"
                                       :key="item.calendarEvent.id"
                                       :show-actions="true"
                                       :item="item"/>
      <v-list-item v-if="tableRows.length === 0 ">
        <v-list-item-content>
          <v-list-item-title>
            <span class="text--secondary">{{ $t('plannedService.calendarEvents.noEventsOnDateRange') }}</span>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <planned-service-event-confirm-change-dialog
        :title="$t('plannedService.leftUndoneDialog.title')"
        :subtitle="$t('plannedService.leftUndoneDialog.subtitle')"
        :confirm-button-label="$t('plannedService.leftUndoneDialog.confirm')"
        v-model="showConfirmSetStateLeftUndone"
        :items="confirmDialogRows"
        @confirm="setStateLeftUndone"/>

    <planned-service-instance-move-to-date-dialog
        ref="moveToDateDialog"
        @done="updateCalendarEvents"/>

    <entity-action-context-menu ref="entityActionContextMenu"/>
  </v-container>
</template>

<script>
import PlannedServiceEventsService from '@/services/PlannedServiceEventsService';
import PlainUnorderedList from '@/components/layouts/PlainUnorderedList';
import PlannedServiceService from '@/services/PlannedServiceService';
import EntityTableSelectedRowsActionMenu from '@/components/entity-crud/EntityTableSelectedRowsActionMenu';
import EntityTableRowEndActionMenu from '@/components/entity-crud/EntityTableRowEndActionMenu';
import i18n from '@/i18n';
import PlannedServiceInstanceMoveToDateDialog
  from '@/views/plannedService/PlannedServiceInstanceMoveToDateDialog';
import DateRangePicker from '@/components/inputs/DateRangePicker';
import EntityViewFilter from '@/components/entity-crud/EntityViewFilter';
import PlannedServiceEventListItem from '@/views/frontpage/planned-service-events-table/PlannedServiceEventListItem';
import PlannedServiceEventConfirmChangeDialog
  from '@/views/frontpage/planned-service-events-table/PlannedServiceEventConfirmChangeDialog';
import EntityActionContextMenu from '@/components/entity-crud/EntityActionContextMenu';


export default {
  name: 'PlannedServiceEvents',
  components: {
    EntityActionContextMenu,
    PlannedServiceEventConfirmChangeDialog,
    PlannedServiceEventListItem,
    EntityViewFilter,
    DateRangePicker,
    PlannedServiceInstanceMoveToDateDialog,
    EntityTableRowEndActionMenu,
    EntityTableSelectedRowsActionMenu,
    PlainUnorderedList,
  },
  data() {
    return {
      selectedRows: [],
      loading: false,
      calendarEvents: [],
      showConfirmSetStateLeftUndone: false,
      confirmDialogRows: [],
      moveToDateItem: null,
    };
  },
  created() {
    this.updateCalendarEvents();
  },
  computed: {
    dateRange: {
      get() {
        return this.$store.state.plannedEventsViewState.dateRange;
      },
      set(value) {
        this.$store.commit('plannedEventsViewState/setDateRange', value);
      },
    },
    includeLate: {
      get() {
        return this.$store.state.plannedEventsViewState.includeLate;
      },
      set(value) {
        this.$store.commit('plannedEventsViewState/setIncludeLate', value);
      },
    },
    onlyCurrentUserAssigned: {
      get() {
        return this.$store.state.plannedEventsViewState.onlyCurrentUserAssigned;
      },
      set(value) {
        this.$store.commit('plannedEventsViewState/setOnlyCurrentUserAssigned', value);
      },
    },

    tableHeaders() {
      return [
        {
          value: 'plannedServicescheduleDate',
          text: this.$t('plannedService.scheduleDate'),
          getValue: calendarEvent => {
            return this.$options.filters.date(calendarEvent.plannedService.scheduleDate);
          },
        },
        {
          value: 'plannedServiceName',
          text: this.$t('plannedService.singular'),
          getValue: calendarEvent => {
            return calendarEvent.plannedService.name;
          },
        },
        {
          value: 'device',
          text: this.$t('plannedService.device'),
          getValue: calendarEvent => {
            return this.$options.filters.device(calendarEvent.plannedService.device);
          },
        },
        {
          value: 'serviceManagers',
          type: 'list',
          text: this.$t('plannedService.serviceManagers'),
          getValue: calendarEvent => {
            return calendarEvent.plannedService.serviceManagers.map(user => this.$options.filters.user(user));
          },
        },
        {
          value: 'authorUsers',
          type: 'list',
          text: this.$t('plannedService.authorUsers'),
          getValue: calendarEvent => {
            return calendarEvent.plannedService.authorUsers.map(user => this.$options.filters.user(user));
          },
        },
        {
          value: 'duration',
          text: this.$t('plannedService.duration'),
          getValue: calendarEvent => {
            return this.$options.filters.duration(calendarEvent.plannedService.durationMinutes);
          },
        },
        {
          value: 'serviceDoneDate',
          text: this.$t('plannedService.serviceDoneDate'),
          getValue: calendarEvent => {
            return this.$options.filters.date(calendarEvent.plannedService.serviceDoneDate);
          },
        },
        {
          value: 'serviceState',
          text: this.$t('service.serviceState'),
          getValue: calendarEvent => {
            return calendarEvent.plannedService.serviceState;
          },
        },
        {
          value: 'priority',
          text: this.$t('plannedService.priority'),
          getValue: calendarEvent => {
            return this.$options.filters.priority(calendarEvent.plannedService.priority);
          },
        },
        {
          value: 'rowActions',
          text: '',
        },
      ];
    },
    tableRows() {
      return this.calendarEvents.map(calendarEvent => {
        let row = {};
        row['calendarEvent'] = calendarEvent;
        row['rowActions'] = this.createRowActions(calendarEvent);
        row.rowClick = () => {
          this.$router.push(`plannedservices/${calendarEvent.plannedService.id}`);
        };

        this.tableHeaders.forEach(header => {
          if (header.getValue) {
            row[header.value] = header.getValue(calendarEvent);
          }
        });
        return row;
      });
    },

    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('plannedService.leftUndoneDialog.title'),
          icon: 'mdi-calendar-remove',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          click: () => {
            this.confirmSetStateLeftUndone(this.selectedRows.map(row => row.calendarEvent));
          },
        },

        {
          label: i18n.t('plannedService.calendarEvents.downloadAsExcelFile'),
          icon: 'mdi-download',
          disabled: this.calendarEvents.length === 0,
          dividerBefore: true,
          click: () => {
            this.downloadAsExcel();
          },
        },
        {
          label: i18n.t('plannedService.calendarEvents.downloadIcalExports'),
          icon: 'mdi-calendar-export',
          disabled: this.calendarEvents.length === 0,
          click: () => {
            this.downloadIcalExport();
          },
        },
      ];

      return actions.filter(action => action.hasPermission);
    },
  },

  methods: {


    createRowActions(calendarEvent) {
      const actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: true,
          click: () => {
            this.$router.push(`plannedservices/${calendarEvent.plannedService.id}`);
          },
          dividerAfter: true,
        },
        {
          label: i18n.t('plannedService.moveToDateDialog.title'),
          icon: 'mdi-calendar-arrow-right',
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          click: () => {
            this.$refs.moveToDateDialog.open(calendarEvent.plannedService.plannedServiceInstanceId, calendarEvent.plannedService.scheduleDate);

          },
        },
        {
          label: i18n.t('plannedService.leftUndoneDialog.title'),
          icon: 'mdi-calendar-remove',
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          click: () => {
            this.confirmSetStateLeftUndone([calendarEvent]);
          },
        },
        {
          label: i18n.t('plannedService.calendarEvents.downloadIcalExport'),
          icon: 'mdi-calendar-export',
          dividerBefore: true,
          click: () => {
            this.downloadIcalExportSingeEvent(calendarEvent);
          },
        },
      ];
      return actions.filter(action => action.hasPermission);
    },

    rowRightClickHandler(event, item) {
      console.log('right click ' + item);
      this.$refs.entityActionContextMenu.show(event, item.item.rowActions);
    },


    confirmSetStateLeftUndone(calendarEvents) {
      this.confirmDialogRows = calendarEvents.map(calendarEvent => {
        return {
          calendarEvent: calendarEvent,
        };
      });
      this.showConfirmSetStateLeftUndone = true;
    },

    updateCalendarEvents() {
      this.loading = true;
      this.calendarEvents = [];
      this.selectedRows = [];
      PlannedServiceEventsService.getFrontpageEvents(this.dateRange.startDate, this.dateRange.endDate, this.onlyCurrentUserAssigned, this.includeLate).then((result) => {
        this.calendarEvents = result.data;
        this.loading = false;
      }).catch(result => {
        console.log(JSON.stringify(result));
        this.loading = false;
      });
    },

    setStateLeftUndone(rows) {
      const ids = rows.map(row => row.calendarEvent.plannedService.plannedServiceInstanceId);
      return PlannedServiceService.setStateLeftUndoneServiceInstances(ids).then(() => {
        this.showConfirmSetStateLeftUndone = false;
        this.updateCalendarEvents();
      }).catch((result) => {
        console.log(JSON.stringify(result));
        this.showConfirmSetStateLeftUndone = false;
      });
    },


    downloadIcalExport() {
      PlannedServiceEventsService.downloadIcalExport(this.dateRange.startDate, this.dateRange.endDate, this.onlyCurrentUserAssigned).catch(result => {
        console.log(JSON.stringify(result));
        this.loading = false;
      });
    },

    downloadIcalExportSingeEvent(calendarEvent) {
      PlannedServiceEventsService.downloadIcalExportForPlannedService(calendarEvent.plannedService.plannedServiceInstanceId)
          .catch(result => {
            console.log(JSON.stringify(result));
          });
    },

    downloadAsExcel() {
      PlannedServiceEventsService.downloadFrontpageEvents(this.dateRange.startDate, this.dateRange.endDate, this.onlyCurrentUserAssigned)
          .catch(result => {
            console.log(JSON.stringify(result));
          });
    },

    moveToDate(calendarEvent) {
      this.$refs.moveToDateDialog.open(calendarEvent.plannedService.plannedServiceInstanceId, calendarEvent.plannedService.scheduleDate);
    },
  },
};
</script>

<style scoped>

</style>
